import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link, useSearchParams } from "react-router-dom";
import {
    KeyRound,
    ArrowLeft,
    CheckCircle,
    AlertCircle,
    LogIn,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/ui/password-input";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useResetPassword } from "@/features/auth/hooks/useResetPassword";

const resetPasswordSchema = z
    .object({
        newPassword: z
            .string()
            .min(6, "Password must be at least 6 characters")
            .max(100, "Password must be at most 100 characters"),
        confirmPassword: z.string().min(1, "Please confirm your password"),
    })
    .refine((data) => data.newPassword === data.confirmPassword, {
        message: "Passwords do not match",
        path: ["confirmPassword"],
    });

type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

export const ResetPasswordPage = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token")?.trim() ?? "";
    const userId = searchParams.get("userId")?.trim() ?? "";

    const linkInvalid = !token || !userId;

    const { isLoading, isSuccess, error, resetPassword, clearError } =
        useResetPassword();

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<ResetPasswordFormData>({
        resolver: zodResolver(resetPasswordSchema),
    });

    const onSubmit = async (data: ResetPasswordFormData) => {
        clearError();
        await resetPassword({
            token,
            userId,
            newPassword: data.newPassword,
        });
    };

    if (linkInvalid) {
        return (
            <div className="min-h-screen flex flex-col bg-background">
                <div className="flex-1 flex items-center justify-center p-4 sm:p-6 lg:p-8">
                    <div className="w-full max-w-md space-y-8">
                        <Card className="shadow-medium">
                            <CardHeader className="text-center pb-4">
                                <div className="mx-auto mb-4 rounded-full bg-destructive/10 p-4 w-fit">
                                    <AlertCircle className="h-8 w-8 text-destructive" />
                                </div>
                                <CardTitle className="text-xl">
                                    Invalid reset link
                                </CardTitle>
                                <CardDescription>
                                    This password reset link is missing required
                                    information. Please use the link from your
                                    email or request a new one.
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="flex flex-col gap-2">
                                <Link to="/forgot-password">
                                    <Button className="w-full h-11">
                                        Request a new reset link
                                    </Button>
                                </Link>
                                <Link to="/login">
                                    <Button
                                        variant="ghost"
                                        className="w-full gap-2"
                                    >
                                        <ArrowLeft className="h-4 w-4" />
                                        Back to sign in
                                    </Button>
                                </Link>
                            </CardContent>
                        </Card>
                    </div>
                </div>

                <footer className="py-6 text-center">
                    <p className="text-sm text-muted-foreground">
                        © {new Date().getFullYear()} My Professional Tax. All
                        rights reserved.
                    </p>
                </footer>
            </div>
        );
    }

    if (isSuccess) {
        return (
            <div className="min-h-screen flex flex-col bg-background">
                <div className="flex-1 flex items-center justify-center p-4 sm:p-6 lg:p-8">
                    <div className="w-full max-w-md space-y-8">
                        <Card className="shadow-medium">
                            <CardHeader className="text-center pb-4">
                                <div className="mx-auto mb-4 rounded-full bg-success/10 p-4 w-fit">
                                    <CheckCircle className="h-8 w-8 text-success" />
                                </div>
                                <CardTitle className="text-xl">
                                    Password updated
                                </CardTitle>
                                <CardDescription>
                                    Your password has been reset. You can sign in
                                    with your new password.
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <Link to="/login">
                                    <Button className="w-full h-11 gap-2">
                                        <LogIn className="h-4 w-4" />
                                        Sign in to your account
                                    </Button>
                                </Link>
                            </CardContent>
                        </Card>
                    </div>
                </div>

                <footer className="py-6 text-center">
                    <p className="text-sm text-muted-foreground">
                        © {new Date().getFullYear()} My Professional Tax. All
                        rights reserved.
                    </p>
                </footer>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col bg-background">
            <div className="flex-1 flex items-center justify-center p-4 sm:p-6 lg:p-8">
                <div className="w-full max-w-md space-y-8">
                    <Card className="shadow-medium">
                        <CardHeader className="text-center pb-4">
                            <div className="mx-auto mb-4 rounded-full bg-primary/10 p-4 w-fit">
                                <KeyRound className="h-8 w-8 text-primary" />
                            </div>
                            <CardTitle className="text-xl">
                                Set a new password
                            </CardTitle>
                            <CardDescription>
                                Choose a new password for{" "}
                                <span className="font-medium text-foreground">
                                    {userId}
                                </span>
                                .
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <form
                                onSubmit={handleSubmit(onSubmit)}
                                className="space-y-5"
                            >
                                <div className="space-y-2">
                                    <Label htmlFor="newPassword">
                                        New password
                                    </Label>
                                    <PasswordInput
                                        id="newPassword"
                                        autoComplete="new-password"
                                        placeholder="Enter new password"
                                        {...register("newPassword")}
                                        className={
                                            errors.newPassword
                                                ? "border-destructive focus-visible:ring-destructive"
                                                : ""
                                        }
                                    />
                                    {errors.newPassword && (
                                        <p className="text-sm text-destructive flex items-center gap-1.5">
                                            <AlertCircle className="h-3.5 w-3.5" />
                                            {errors.newPassword.message}
                                        </p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="confirmPassword">
                                        Confirm password
                                    </Label>
                                    <PasswordInput
                                        id="confirmPassword"
                                        autoComplete="new-password"
                                        placeholder="Confirm new password"
                                        {...register("confirmPassword")}
                                        className={
                                            errors.confirmPassword
                                                ? "border-destructive focus-visible:ring-destructive"
                                                : ""
                                        }
                                    />
                                    {errors.confirmPassword && (
                                        <p className="text-sm text-destructive flex items-center gap-1.5">
                                            <AlertCircle className="h-3.5 w-3.5" />
                                            {errors.confirmPassword.message}
                                        </p>
                                    )}
                                </div>

                                {error && (
                                    <Alert variant="destructive">
                                        <AlertCircle className="h-4 w-4" />
                                        <AlertDescription>
                                            {error}
                                        </AlertDescription>
                                    </Alert>
                                )}

                                <Button
                                    type="submit"
                                    className="w-full h-11"
                                    loading={isLoading}
                                    loadingLabel="Updating..."
                                >
                                    Update password
                                </Button>

                                <Link to="/login">
                                    <Button
                                        variant="ghost"
                                        className="w-full gap-2"
                                    >
                                        <ArrowLeft className="h-4 w-4" />
                                        Back to sign in
                                    </Button>
                                </Link>
                            </form>
                        </CardContent>
                    </Card>
                </div>
            </div>

            <footer className="py-6 text-center">
                <p className="text-sm text-muted-foreground">
                    © {new Date().getFullYear()} My Professional Tax. All rights
                    reserved.
                </p>
            </footer>
        </div>
    );
};
